import React from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { analyticsLabels } from '@/components/global-nav/user-profile-new/src/user-profile';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { AtlassianLogoArrowLinkBrand } from '@/libraries/wpl/foundations/src/logos/atlassian-logo-arrow-link-brand';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useContactSalesCtaEligibility, ContactSalesCta } from '../components/contact-sales-cta';
import { GlobalNavLinkContentProps } from '../nav-links-container';
import SearchButton from '../search-button';

import { Divider } from './icons/divider';
import { DropdownType } from './mobile-nav';
import { MobileNavLink } from './mobile-nav-link';

const linkContainerCss = css({
	listStyle: 'none',
	padding: '0 0 24px 0',
	margin: '0',
	fontWeight: '700',
	fontSize: '14px',
});

const signInContainerCss = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	paddingBlockStart: '8px',
	paddingBlockEnd: '8px',
	[media.above.sm]: {
		display: 'none',
	},
});

const returnToHomeStyle = css({
	display: 'none',
	[media.below.sm]: {
		display: 'block',
	},
});

const getItFreeCss = css({
	display: 'none',
	[media.below.sm]: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBlockStart: '8px',
		marginBlockEnd: '8px',
		height: '36px',
	},
});

const atlassianArrowLinkContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlockStart: '32px',
});

const signInCss = css({
	paddingBlock: token('space.400'),
	alignItems: 'center',
	display: 'inline-flex',
	color: wacToken('color.extended.blue700'),
	textDecoration: 'none',
	'&:visited': {
		color: wacToken('color.extended.purple800'),
	},
	'&:hover, &:focus': {
		color: wacToken('color.extended.blue800'),
		textShadow: 'none',
	},
	'&:visited:hover, &:visited:focus': {
		color: wacToken('color.extended.purple900'),
	},
	'&:focus-visible': {
		border: 'none',
		borderRadius: '3px',
		outlineStyle: 'solid',
		outlineOffset: '2px',
		outlineWidth: '2px',
		outlineColor: wacToken('color.primary.blue'),
	},
});

export type MobileNavLinkContentProps = {
	readonly globalLinksKey: string;
	readonly linkText: string;
	readonly hasSubnav: boolean;
	readonly href?: string;
	readonly productKey?: string;
	readonly dropdownType?: DropdownType | string;
};

export type MobileNavLinksContainerInfo = {
	mobileLinkKey: string;
	mobileLinksProps: MobileNavLinkContentProps[];
};

interface MobileNavLinksContainerProps extends MobileNavLinksContainerInfo {
	handleMenuPage: (menuPage: DropdownType | string) => void;
	isIncludeBottomDivider?: boolean;
}

export const MobileNavLinksContainer = ({
	handleMenuPage,
	mobileLinkKey,
	mobileLinksProps,
	isIncludeBottomDivider = true,
}: MobileNavLinksContainerProps) => {
	return (
		<>
			<ul className={mobileLinkKey} css={linkContainerCss}>
				{mobileLinksProps.map((mobileLink: GlobalNavLinkContentProps, index) => {
					return (
						<li className="nav-link" key={`${mobileLink.globalLinksKey}-${index}`}>
							<MobileNavLink handleMenuPage={handleMenuPage} {...mobileLink} />
						</li>
					);
				})}
			</ul>
			{isIncludeBottomDivider && <Divider />}
		</>
	);
};

type MobileNavLinksProps = {
	isLoggedIn: boolean;
	handleMenuPage: (menuPage: DropdownType | string) => void;
	mobileLinksSections: MobileNavLinksContainerInfo[];
	productKey?: ProductKeyTypes;
	isIncludeCTA?: boolean;
	ctaText?: string;
	ctaUrl?: string;
	ctaOnClick?: (e: React.MouseEvent) => void;
	ctaButtonVariant?: 'primary' | 'secondary';
	isIncludeAtlassianLogo?: boolean;
	isShowSearchButton?: boolean;
};

export const MobileNavLinks = ({
	isLoggedIn,
	handleMenuPage,
	mobileLinksSections,
	productKey = 'jira',
	isIncludeCTA = false,
	ctaText = 'Get it free',
	ctaUrl = '/software/jira/free',
	ctaOnClick,
	ctaButtonVariant = 'primary',
	isIncludeAtlassianLogo = false,
	isShowSearchButton = true,
}: MobileNavLinksProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const baseUrl = process.env['NEXT_PUBLIC_IDENTITY_BASE_URL'];
	const stargateBaseUrl = process.env['NEXT_PUBLIC_API_PRIVATE_BASE_URL'];
	const continueUrl = stargateBaseUrl + '/start/authredirect';

	const isWacIcLoginEnabled = checkFeatureFlagGate(featureFlags.wacIcLogin);
	const applicationParam = isWacIcLoginEnabled ? '&application=wac--ic' : '';

	const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
		continueUrl,
	)}${applicationParam}`;

	const homeHref = useLocalizedHref('/');
	const signinLink = {
		text: intl.formatMessage({
			id: 'globalnavv2.mobile.navlink.signin',
			defaultMessage: 'Sign in',
		}),
		href: signInUrl,
	};

	const onLogInClick = () => {
		createAnalyticsEvent({
			action: 'clicked',
			name: 'bxp.globalnavdropdown.click.login',
			label: analyticsLabels['JoinSiteUserDropdown.logIn'],
		}).fire();
	};

	// Mobile search improvements: hide search button (moved to top of menu)
	isShowSearchButton = false;

	const { isEligible: showContactSalesCta } = useContactSalesCtaEligibility();

	return (
		<div>
			{showContactSalesCta ? (
				<div css={getItFreeCss}>
					<ContactSalesCta isFullWidth={true} />
				</div>
			) : (
				isIncludeCTA && (
					<div css={getItFreeCss}>
						<Adg4Button
							className="mobile-nav-dropdown-menu-cta"
							variant={ctaButtonVariant}
							href={ctaOnClick ? undefined : ctaUrl}
							dataEventProps={{
								'data-product-key': productKey,
								'data-event': 'clicked',
								'data-event-component': 'linkButton',
								'data-event-container': 'navbar',
								'data-label': ctaText,
								'data-label-english': 'Get it free',
								'data-track-event': `${productKey}.get-it-free-button-clicked`,
							}}
							style={{
								width: '100%',
								fontSize: '1rem',
								lineHeight: '1.25rem',
							}}
							onClick={ctaOnClick}
						>
							{ctaText}
						</Adg4Button>
					</div>
				)
			)}
			{mobileLinksSections.map(({ mobileLinkKey, mobileLinksProps }, index) => (
				<React.Fragment key={index}>
					<MobileNavLinksContainer
						mobileLinkKey={mobileLinkKey}
						mobileLinksProps={mobileLinksProps}
						handleMenuPage={handleMenuPage}
						isIncludeBottomDivider={false}
					/>
				</React.Fragment>
			))}
			{/* Divider above Atlassian logo for mobile search improvements */}
			<Divider />
			{isIncludeAtlassianLogo && (
				<div css={returnToHomeStyle}>
					<div css={atlassianArrowLinkContainerStyle}>
						<a
							className="navigation-bar-atlas-icon"
							href={homeHref}
							aria-label={intl.formatMessage({
								id: 'productPageNav.atlassian-logo-label',
								defaultMessage: 'Atlassian logo',
							})}
							data-event="clicked"
							data-event-component="linkButton"
							data-event-container="productPageMobileNav"
							data-product-key="home"
						>
							<AtlassianLogoArrowLinkBrand width={'311px'} height={'15.29px'} />
						</a>
					</div>
				</div>
			)}
			<div css={signInContainerCss}>
				{isShowSearchButton && (
					<SearchButton
						buttonId={'global-nav-search-bar-icon-mobile'}
						onClick={(e) => {
							e.preventDefault();
							handleMenuPage('search');

							createAnalyticsEvent({
								eventName: 'searchOpen',
								eventType: 'ui',
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'searchButtonMobile',
								source: 'globalNav',
								attributes: {
									product: 'search',
								},
							}).fire();
						}}
					/>
				)}
				{!isLoggedIn && (
					<a
						href={signinLink.href}
						css={signInCss}
						onClick={onLogInClick}
						aria-label={intl.formatMessage({
							id: 'globalnavv2.mobile.navlink.signin.ariaLabel',
							defaultMessage: 'Sign in link',
						})}
						data-product-key={productKey}
						data-event-component="linkButton"
						data-event-container="navbarDropdown"
						data-label={signinLink.text}
						data-event="clicked"
					>
						{signinLink.text}
					</a>
				)}
			</div>
		</div>
	);
};
